import React, { useState } from 'react';

/**
 * VocabularyConstrainedInput - Renders appropriate input based on vocabulary field type
 *
 * Field types:
 * - enum: Single selection from allowed values (dropdown)
 * - range: Numeric value within min/max bounds (number input)
 * - list: Multiple selection from allowed values (multi-select)
 * - boolean: True/false (checkbox or toggle)
 * - text: Free-form text (text input)
 */

// ============================================================================
// Enum Select Component
// ============================================================================
function EnumSelect({ field, value, onChange, disabled }) {
  const options = field.enumValues || [];

  // Format enum value for display (replace underscores with spaces, capitalize)
  const formatEnumValue = (val) => {
    if (!val) return '';
    return val
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(field.fieldName, e.target.value || null)}
      disabled={disabled}
      className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
    >
      <option value="">Select {field.displayName}...</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {formatEnumValue(opt)}
        </option>
      ))}
    </select>
  );
}

// ============================================================================
// Range Number Input Component
// ============================================================================
function RangeInput({ field, value, onChange, disabled }) {
  const [localValue, setLocalValue] = useState(value ?? '');
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);

    if (newValue === '') {
      setError(null);
      onChange(field.fieldName, null);
      return;
    }

    const numValue = parseFloat(newValue);

    if (isNaN(numValue)) {
      setError('Must be a number');
      return;
    }

    if (field.rangeMin !== undefined && numValue < field.rangeMin) {
      setError(`Minimum: ${field.rangeMin}`);
    } else if (field.rangeMax !== undefined && numValue > field.rangeMax) {
      setError(`Maximum: ${field.rangeMax}`);
    } else {
      setError(null);
    }

    onChange(field.fieldName, numValue);
  };

  const handleBlur = () => {
    // Clamp value on blur if out of range
    if (localValue !== '' && !error) {
      const numValue = parseFloat(localValue);
      let clampedValue = numValue;

      if (field.rangeMin !== undefined && numValue < field.rangeMin) {
        clampedValue = field.rangeMin;
      } else if (field.rangeMax !== undefined && numValue > field.rangeMax) {
        clampedValue = field.rangeMax;
      }

      if (clampedValue !== numValue) {
        setLocalValue(clampedValue.toString());
        onChange(field.fieldName, clampedValue);
        setError(null);
      }
    }
  };

  return (
    <div className="flex-1">
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          min={field.rangeMin}
          max={field.rangeMax}
          step={field.rangeUnit?.includes('ft') || field.rangeUnit?.includes('inch') ? '0.1' : '1'}
          disabled={disabled}
          className={`flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${
            error ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-700'
          }`}
        />
        {field.rangeUnit && (
          <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">{field.rangeUnit}</span>
        )}
      </div>
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
      {!error && field.rangeMin !== undefined && field.rangeMax !== undefined && (
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
          Range: {field.rangeMin} - {field.rangeMax}
        </p>
      )}
    </div>
  );
}

// ============================================================================
// List Multi-Select Component
// ============================================================================
function ListMultiSelect({ field, value, onChange, disabled }) {
  const options = field.listValues || [];
  const selectedValues = Array.isArray(value) ? value : [];

  const handleToggle = (option) => {
    if (disabled) return;

    const newValues = selectedValues.includes(option)
      ? selectedValues.filter((v) => v !== option)
      : [...selectedValues, option];

    onChange(field.fieldName, newValues.length > 0 ? newValues : null);
  };

  // Format list value for display
  const formatListValue = (val) => {
    if (!val) return '';
    return val
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const isSelected = selectedValues.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => handleToggle(opt)}
              disabled={disabled}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                isSelected
                  ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-700'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              {isSelected && (
                <svg className="w-3 h-3 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
              {formatListValue(opt)}
            </button>
          );
        })}
      </div>
      {selectedValues.length > 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {selectedValues.length} selected
        </p>
      )}
    </div>
  );
}

// ============================================================================
// Boolean Checkbox Component
// ============================================================================
function BooleanCheckbox({ field, value, onChange, disabled }) {
  const handleChange = (e) => {
    onChange(field.fieldName, e.target.checked);
  };

  return (
    <label className={`flex items-center gap-2 ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
      <input
        type="checkbox"
        checked={value === true}
        onChange={handleChange}
        disabled={disabled}
        className="w-4 h-4 text-blue-600 dark:text-blue-400 border-gray-300 dark:border-gray-700 rounded focus:ring-blue-500 disabled:cursor-not-allowed"
      />
      <span className="text-sm text-gray-700 dark:text-gray-200">Yes</span>
    </label>
  );
}

// ============================================================================
// Text Input Component
// ============================================================================
function TextInput({ field, value, onChange, disabled }) {
  return (
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(field.fieldName, e.target.value || null)}
      disabled={disabled}
      placeholder={`Enter ${(field.displayName || field.fieldName || 'value').toLowerCase()}...`}
      className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
    />
  );
}

// ============================================================================
// Main VocabularyConstrainedInput Component
// ============================================================================
export function VocabularyConstrainedInput({
  field,
  value,
  onChange,
  disabled = false,
  showLabel = true,
  showDescription = false,
}) {
  if (!field) return null;

  const renderInput = () => {
    switch (field.fieldType) {
      case 'enum':
        return <EnumSelect field={field} value={value} onChange={onChange} disabled={disabled} />;
      case 'range':
        return <RangeInput field={field} value={value} onChange={onChange} disabled={disabled} />;
      case 'list':
        return <ListMultiSelect field={field} value={value} onChange={onChange} disabled={disabled} />;
      case 'boolean':
        return <BooleanCheckbox field={field} value={value} onChange={onChange} disabled={disabled} />;
      case 'text':
      default:
        return <TextInput field={field} value={value} onChange={onChange} disabled={disabled} />;
    }
  };

  return (
    <div className="space-y-1">
      {showLabel && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
          {field.displayName}
        </label>
      )}
      {showDescription && field.description && (
        <p className="text-xs text-gray-500 dark:text-gray-400">{field.description}</p>
      )}
      {renderInput()}
    </div>
  );
}

export default VocabularyConstrainedInput;
